/* ==========================================================================
   Birthstone Ring Configurator
   - Reads stone + compositing data from embedded JSON
   - Renders stone picker grids inside each slot
   - Shows/hides slots based on the "Number of Stones" variant option
   - COMPOSITING: layers transparent stone PNGs over a base ring image
   - Swaps base ring image when metal color or stone count changes
   - Validates all visible slots have a selection before add-to-cart
   - Writes a summary string into a hidden line item property
   ========================================================================== */

(function () {
  "use strict";

  function initBirthstoneConfigurator(dataEl) {
    if (!dataEl) return;

    var sectionId = dataEl.getAttribute("data-section-id");
    var productId = dataEl.getAttribute("data-product-id");
    if (!sectionId || !productId) return;

    var rootId = "ProductSection-" + sectionId + "-" + productId;
    var root = document.getElementById(rootId);
    if (!root) return;

    var config;
    try {
      config = JSON.parse(dataEl.textContent || "{}");
    } catch (e) {
      console.error("[BIRTHSTONE] Failed to parse stone data JSON", e);
      return;
    }

    var stones = config.stones || [];
    var maxStones = config.maxStones || 6;
    var baseImages = config.baseImages || {};
    var positionMap = config.positionMap || {};
    var defaultStonesMF = Array.isArray(config.defaultStones) ? config.defaultStones : [];
    var defaultSizeMF = (config.defaultSize || "").toString();
    if (!stones.length) return;

    // Sort stones by birth month (Jan → Dec) so grid/accordion swatches
    // always appear in calendar order, regardless of how
    // shop.metaobjects.birthstone.values returns them (creation order,
    // handle order, or admin drag-sort).
    stones.sort(function (a, b) {
      return (Number(a.month) || 99) - (Number(b.month) || 99);
    });

    // Build stone lookup by id AND by month (1-12)
    var stoneById = {};
    var stoneByMonth = {};
    stones.forEach(function (s) {
      stoneById[s.id] = s;
      if (s.month) stoneByMonth[s.month] = s;
    });

    // ------------------------------------------------------------------
    // State
    // ------------------------------------------------------------------
    var stoneCount = 0;
    var selections = {};   // { 1: "garnet", 2: "ruby", ... }
    var currentMetal = "";  // "yellow", "white", "rose"

    // ------------------------------------------------------------------
    // DOM refs
    // ------------------------------------------------------------------
    var form = root.querySelector("[data-birthstone-form]");
    var optionsEl = root.querySelector("[data-birthstone-options]");
    var pickerStyle = optionsEl ? (optionsEl.getAttribute("data-picker-style") || "grid") : "grid";
    var isAccordion = pickerStyle === "accordion";
    var slotsContainer = root.querySelector("[data-birthstone-slots]");
    var validationMsg = root.querySelector("[data-birthstone-validation]");
    var summaryInput = root.querySelector("[data-birthstone-summary]");
    var configCodeInput = root.querySelector("[data-birthstone-config-code]");
    var sizeSelect = root.querySelector("#BirthstoneSize");

    // Compositing DOM refs
    var customizer = root.querySelector("[data-ring-customizer]");
    var baseImg = root.querySelector("[data-ring-base]");
    var themeGallery = root.querySelector("[data-product-images]");
    var mainPhotos = themeGallery ? themeGallery.querySelector(".product__main-photos") : null;
    var gallerySlideshow = mainPhotos ? mainPhotos.querySelector("[data-product-photos]") : null;
    var metalDescEl = root.querySelector("[data-metal-description]");

    // Relocate the customizer into the main-photos slot so it overlays the
    // hero image while thumbnails (rendered beside) stay visible. Falls back
    // to the original location if the gallery isn't present.
    if (customizer && mainPhotos && customizer.parentNode !== mainPhotos) {
      customizer.style.position = "absolute";
      customizer.style.top = "0";
      customizer.style.left = "0";
      customizer.style.zIndex = "2";
      mainPhotos.insertBefore(customizer, mainPhotos.firstChild);
      if (getComputedStyle(mainPhotos).position === "static") {
        mainPhotos.style.position = "relative";
      }
    }

    // Whether the shopper clicked a real gallery thumbnail to browse
    // product photos. Resets on any picker change.
    var userViewingGallery = false;

    if (!slotsContainer) return;

    // ------------------------------------------------------------------
    // Render swatch buttons into each picker grid
    // ------------------------------------------------------------------
    for (var s = 1; s <= maxStones; s++) {
      renderPickerGrid(s);
    }

    function renderPickerGrid(slotNum) {
      var picker = root.querySelector('[data-birthstone-picker="' + slotNum + '"]');
      if (!picker) return;

      picker.innerHTML = "";

      stones.forEach(function (stone) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "birthstone-swatch";
        btn.setAttribute("data-stone-id", stone.id);
        btn.setAttribute("data-slot-num", slotNum);
        btn.setAttribute("aria-label", stone.name + " (" + stone.label + ")");

        var circle = document.createElement("span");
        circle.className = "birthstone-swatch__circle";

        if (stone.image) {
          var img = document.createElement("img");
          img.src = stone.image;
          img.alt = stone.name;
          img.loading = "lazy";
          img.decoding = "async";
          circle.appendChild(img);
        } else {
          circle.style.backgroundColor = stone.hex || "#ccc";
        }

        var nameSpan = document.createElement("span");
        nameSpan.className = "birthstone-swatch__name";
        nameSpan.textContent = isAccordion ? (stone.label || stone.name) : stone.name;

        btn.appendChild(circle);
        btn.appendChild(nameSpan);

        btn.addEventListener("click", function () {
          selectStone(slotNum, stone.id, stone.name);
        });

        picker.appendChild(btn);
      });
    }

    // ------------------------------------------------------------------
    // Accordion helpers (only used when pickerStyle === "accordion")
    // ------------------------------------------------------------------
    function setAccordionOpen(slotNum, open) {
      var slot = root.querySelector('[data-birthstone-slot="' + slotNum + '"]');
      if (!slot) return;
      var body = slot.querySelector("[data-accordion-body]");
      var icon = slot.querySelector("[data-slot-header-icon]");
      var toggle = slot.querySelector("[data-accordion-toggle]");

      slot.setAttribute("data-accordion-open", open ? "true" : "false");
      if (body) {
        if (open) body.removeAttribute("hidden");
        else body.setAttribute("hidden", "");
      }
      if (icon) icon.textContent = open ? "\u2212" : "+";
      if (toggle) toggle.setAttribute("aria-expanded", open ? "true" : "false");
    }

    function openOnlyAccordion(slotNum) {
      for (var i = 1; i <= maxStones; i++) {
        setAccordionOpen(i, i === slotNum);
      }
    }

    function toggleAccordion(slotNum) {
      var slot = root.querySelector('[data-birthstone-slot="' + slotNum + '"]');
      if (!slot) return;
      var isOpen = slot.getAttribute("data-accordion-open") === "true";
      if (isOpen) {
        setAccordionOpen(slotNum, false);
      } else {
        openOnlyAccordion(slotNum);
      }
    }

    function updateAccordionHeader(slotNum, stoneId) {
      var slot = root.querySelector('[data-birthstone-slot="' + slotNum + '"]');
      if (!slot) return;
      var circle = slot.querySelector("[data-slot-header-circle]");
      var value = slot.querySelector("[data-slot-header-value]");
      var stone = stoneId ? stoneById[stoneId] : null;

      if (circle) {
        circle.innerHTML = "";
        if (stone && stone.image) {
          var img = document.createElement("img");
          img.src = stone.image;
          img.alt = stone.name;
          img.loading = "lazy";
          img.decoding = "async";
          circle.appendChild(img);
          circle.style.backgroundColor = "";
        } else if (stone) {
          circle.style.backgroundColor = stone.hex || "#ccc";
        } else {
          circle.style.backgroundColor = "";
        }
      }

      if (value) {
        value.textContent = stone ? stone.name : "Select a stone";
      }
    }

    if (isAccordion) {
      for (var accNum = 1; accNum <= maxStones; accNum++) {
        var slotEl = root.querySelector('[data-birthstone-slot="' + accNum + '"]');
        if (!slotEl) continue;
        var toggleBtn = slotEl.querySelector("[data-accordion-toggle]");
        if (toggleBtn) {
          (function (num) {
            toggleBtn.addEventListener("click", function (e) {
              e.preventDefault();
              toggleAccordion(num);
            });
          })(accNum);
        }
      }
    }

    // ------------------------------------------------------------------
    // Stone selection
    // ------------------------------------------------------------------
    function selectStone(slotNum, stoneId, stoneName) {
      userViewingGallery = false;
      selections[slotNum] = stoneId;

      // Update hidden input
      var input = root.querySelector('[data-birthstone-input="' + slotNum + '"]');
      if (input) input.value = stoneName;

      // Update active state on swatch buttons
      var picker = root.querySelector('[data-birthstone-picker="' + slotNum + '"]');
      if (picker) {
        var allBtns = picker.querySelectorAll(".birthstone-swatch");
        allBtns.forEach(function (b) {
          b.classList.toggle("is-active", b.getAttribute("data-stone-id") === stoneId);
        });
      }

      if (isAccordion) {
        updateAccordionHeader(slotNum, stoneId);
      }

      syncSummary();
      syncConfigCode();
      syncURL();
      updateComposite();
      hideValidation();
    }

    // ==================================================================
    // METAL COLOR DETECTION (from variant UI)
    // ==================================================================
    function normalizeMetal(raw) {
      var v = String(raw || "").toLowerCase().trim()
        .replace(/-/g, " ").replace(/_/g, " ").replace(/\s+/g, " ");

      if (v.includes("white")) return "white";
      if (v.includes("rose")) return "rose";
      if (v.includes("yellow")) return "yellow";
      if (v.includes("silver")) return "silver";
      return null;
    }

    function getSelectedMetalColor() {
      // Check fieldsets (button-style variant picker)
      var fieldsets = root.querySelectorAll("fieldset");
      for (var i = 0; i < fieldsets.length; i++) {
        var fs = fieldsets[i];
        var legend = fs.querySelector("legend");
        var legendText = (legend ? legend.textContent : "").toLowerCase().trim();

        var isColorField =
          legendText.indexOf("color") !== -1 ||
          legendText.indexOf("colour") !== -1 ||
          legendText.indexOf("metal color") !== -1;

        if (!isColorField) continue;

        var checked = fs.querySelector('input[type="radio"]:checked');
        if (checked) {
          var m = normalizeMetal(checked.value);
          if (m) return m;
        }
      }

      // Check select dropdowns
      var selects = root.querySelectorAll("select");
      for (var j = 0; j < selects.length; j++) {
        var sel = selects[j];
        if (sel.id === "BirthstoneSize" || sel.hasAttribute("data-product-select")) continue;

        var label = "";
        var parent = sel.closest(".variant-wrapper, .variant-input-wrap");
        if (parent) {
          var lbl = parent.querySelector("label, legend, .variant__label");
          if (lbl) label = (lbl.textContent || "").toLowerCase().trim();
        }

        if (label.indexOf("color") !== -1 || label.indexOf("colour") !== -1) {
          var metal = normalizeMetal(sel.value);
          if (metal) return metal;
        }
      }

      // Fallback: current variant JSON
      var variantEl = root.querySelector("[data-current-variant-json]");
      if (variantEl) {
        try {
          var variant = JSON.parse(variantEl.value || variantEl.textContent || "{}");
          var opts = [variant.option1, variant.option2, variant.option3];
          for (var k = 0; k < opts.length; k++) {
            var mc = normalizeMetal(opts[k]);
            if (mc) return mc;
          }
        } catch (e) {}
      }

      return "yellow"; // default fallback
    }

    // ------------------------------------------------------------------
    // Read stone count from the variant option UI
    // ------------------------------------------------------------------
    function getStoneCountFromVariant() {
      var count = 0;

      // Try radio buttons first (button-style variant picker)
      var radios = root.querySelectorAll('input[type="radio"]:checked');
      for (var i = 0; i < radios.length; i++) {
        var val = (radios[i].value || "").trim();
        var num = parseInt(val, 10);
        if (num >= 1 && num <= maxStones) {
          var fieldset = radios[i].closest("fieldset");
          if (fieldset) {
            var legend = fieldset.querySelector("legend");
            var legendText = (legend ? legend.textContent : "").toLowerCase().trim();
            if (legendText.indexOf("stone") !== -1 || legendText.indexOf("number") !== -1) {
              count = num;
              break;
            }
          }
        }
      }

      if (count) return count;

      // Try select dropdowns
      var selects = root.querySelectorAll("select");
      for (var j = 0; j < selects.length; j++) {
        var sel = selects[j];
        if (sel.id === "BirthstoneSize" || sel.hasAttribute("data-product-select")) continue;

        var label = "";
        if (sel.id) {
          var labelEl = root.querySelector('label[for="' + sel.id + '"]');
          if (labelEl) label = (labelEl.textContent || "").toLowerCase().trim();
        }
        if (!label) {
          var parent = sel.closest(".variant-wrapper, .variant-input-wrap");
          if (parent) {
            var lbl = parent.querySelector("label, legend, .variant__label");
            if (lbl) label = (lbl.textContent || "").toLowerCase().trim();
          }
        }

        if (label.indexOf("stone") !== -1 || label.indexOf("number") !== -1) {
          var selVal = parseInt(sel.value, 10);
          if (selVal >= 1 && selVal <= maxStones) {
            count = selVal;
            break;
          }
        }
      }

      if (count) return count;

      // Fallback: current variant JSON
      var variantEl = root.querySelector("[data-current-variant-json]");
      if (variantEl) {
        try {
          var variant = JSON.parse(variantEl.value || variantEl.textContent || "{}");
          var opts = [];
          if (variant.option1) opts.push(variant.option1);
          if (variant.option2) opts.push(variant.option2);
          if (variant.option3) opts.push(variant.option3);

          for (var k = 0; k < opts.length; k++) {
            var n = parseInt(opts[k], 10);
            if (n >= 1 && n <= maxStones && String(n) === opts[k].trim()) {
              count = n;
              break;
            }
          }
        } catch (e) {}
      }

      return count;
    }

    // ==================================================================
    // COMPOSITING — base ring + stone overlay layers
    // ==================================================================
    function clearLayer(layerImg) {
      // Hide the layer AND strip src — setting src="" would trigger the
      // browser to request the current page URL and render a broken-image icon.
      if (!layerImg) return;
      layerImg.style.display = "none";
      layerImg.removeAttribute("src");
      layerImg.alt = "";
    }

    // Hide any layer whose overlay URL fails to load (e.g., 404) so the
    // broken-image icon never shows.
    root.querySelectorAll("[data-ring-stone-layer]").forEach(function (img) {
      img.addEventListener("error", function () {
        clearLayer(img);
      });
    });

    function hasCompositingAssets() {
      // Check if any base images or stone overlays are configured
      for (var key in baseImages) {
        if (baseImages[key]) return true;
      }
      return false;
    }

    function setGalleryCompositorClass(on) {
      var photosEl = themeGallery ? themeGallery.querySelector(".product__photos") : null;
      if (!photosEl) return;
      photosEl.classList.toggle("birthstone-compositor-visible", !!on);
    }

    function showCompositor() {
      if (!customizer || !hasCompositingAssets()) return;
      if (userViewingGallery) return; // shopper is browsing photos
      customizer.style.display = "";
      if (gallerySlideshow) {
        gallerySlideshow.style.visibility = "hidden";
      } else if (themeGallery) {
        themeGallery.style.display = "none"; // fallback: no main-photos slot
      }
      setGalleryCompositorClass(true);
    }

    function hideCompositor() {
      if (customizer) customizer.style.display = "none";
      if (gallerySlideshow) {
        gallerySlideshow.style.visibility = "";
      } else if (themeGallery) {
        themeGallery.style.display = "";
      }
      setGalleryCompositorClass(false);
    }

    function updateComposite() {
      if (!hasCompositingAssets()) return;

      var metal = getSelectedMetalColor();
      currentMetal = metal;

      // Update base ring image
      var baseKey = stoneCount + "-" + metal;
      var baseSrc = baseImages[baseKey] || "";

      if (!baseSrc) {
        hideCompositor();
        return;
      }

      showCompositor();

      if (baseImg) {
        if (baseImg.getAttribute("src") !== baseSrc) {
          baseImg.src = baseSrc;
        }
        baseImg.style.display = "block";
      }

      // Determine which physical positions are used for this stone count
      var positions = positionMap[String(stoneCount)] || [];

      // Update each stone overlay layer
      for (var layer = 1; layer <= maxStones; layer++) {
        var layerImg = root.querySelector('[data-ring-stone-layer="' + layer + '"]');
        if (!layerImg) continue;

        // Is this layer active for the current stone count?
        if (layer <= positions.length) {
          var slotNum = layer;
          var stoneId = selections[slotNum];
          var position = positions[layer - 1]; // physical position on ring

          if (stoneId && stoneById[stoneId]) {
            var stone = stoneById[stoneId];
            // Overlay key is "{position}-{stoneCount}" — different stone counts
            // have physically different layouts, so each (position, count) pair
            // needs its own overlay PNG.
            var overlayKey = position + "-" + stoneCount;
            var overlayUrl = (stone.overlays && stone.overlays[overlayKey]) || "";

            if (overlayUrl) {
              layerImg.src = overlayUrl;
              layerImg.alt = stone.name;
              layerImg.style.display = "";
            } else {
              clearLayer(layerImg);
            }
          } else {
            // Slot visible but no stone selected yet
            clearLayer(layerImg);
          }
        } else {
          // Layer not active for this stone count
          clearLayer(layerImg);
        }
      }
    }

    // ------------------------------------------------------------------
    // Show/hide stone slots + update composite
    // ------------------------------------------------------------------
    function update() {
      var newCount = getStoneCountFromVariant();
      var newMetal = getSelectedMetalColor();
      var countChanged = newCount !== stoneCount;
      var metalChanged = newMetal !== currentMetal;

      stoneCount = newCount;
      currentMetal = newMetal;

      if (countChanged) {
        for (var i = 1; i <= maxStones; i++) {
          var slot = root.querySelector('[data-birthstone-slot="' + i + '"]');
          if (!slot) continue;

          if (i <= stoneCount) {
            slot.style.display = "";
            // Fill any visible-but-empty slot with a default so the ring
            // is never previewed bare.
            if (!selections[i]) applyDefaultToSlot(i);
          } else {
            slot.style.display = "none";

            // Clear selection for hidden slots
            if (selections[i]) {
              delete selections[i];
              var input = root.querySelector('[data-birthstone-input="' + i + '"]');
              if (input) input.value = "";
              var picker = root.querySelector('[data-birthstone-picker="' + i + '"]');
              if (picker) {
                picker.querySelectorAll(".birthstone-swatch").forEach(function (b) {
                  b.classList.remove("is-active");
                });
              }
              if (isAccordion) updateAccordionHeader(i, null);
            }

            if (isAccordion) setAccordionOpen(i, false);
          }
        }

        // Accordion: ensure exactly one visible slot is open
        if (isAccordion && stoneCount > 0) {
          var anyOpen = false;
          for (var k = 1; k <= stoneCount; k++) {
            var s = root.querySelector('[data-birthstone-slot="' + k + '"]');
            if (s && s.getAttribute("data-accordion-open") === "true") {
              anyOpen = true;
              break;
            }
          }
          if (!anyOpen) openOnlyAccordion(1);
        }

        syncSummary();
      }

      if (countChanged || metalChanged) {
        updateComposite();
      }

      // Variant changes alter the SKU → refresh code + URL every cycle
      syncConfigCode();
      syncURL();
    }

    // ------------------------------------------------------------------
    // Summary for hidden line item property
    // ------------------------------------------------------------------
    function syncSummary() {
      if (!summaryInput) return;

      var parts = [];
      for (var i = 1; i <= stoneCount; i++) {
        var input = root.querySelector('[data-birthstone-input="' + i + '"]');
        var val = input ? (input.value || "").trim() : "";
        if (val) parts.push(val);
      }

      summaryInput.value = parts.join(", ");
    }

    // ==================================================================
    // Config Code + URL sync
    //   Code format: {metal_slug}_{month1}_{month2}_..._{monthN}
    //   Example:     yellow_gold_1_2_11_3_6
    //   metal_slug is sourced from the variant metafield
    //   `birthstone.metal_slug` (NOT the native SKU field). The map is
    //   emitted by Liquid as { variantId: "yellow_gold", ... }.
    // ==================================================================
    var metalSlugs = (config.metalSlugs && typeof config.metalSlugs === "object") ? config.metalSlugs : {};
    var allVariants = [];
    (function () {
      var ta = root.querySelector("[data-variant-json]");
      if (!ta) return;
      try {
        allVariants = JSON.parse(ta.value || ta.textContent || "[]") || [];
      } catch (e) {}
    })();

    function getCurrentVariantId() {
      try {
        var id = new URL(window.location.href).searchParams.get("variant");
        if (id) return String(id);
      } catch (e) {}
      var cv = root.querySelector("[data-current-variant-json]");
      if (cv) {
        try {
          var v = JSON.parse(cv.value || cv.textContent || "{}");
          if (v && v.id) return String(v.id);
        } catch (e) {}
      }
      if (allVariants.length) return String(allVariants[0].id);
      return "";
    }

    function getCurrentMetalSlug() {
      var id = getCurrentVariantId();
      if (!id) return "";
      return String(metalSlugs[id] || "").toLowerCase().trim();
    }

    function findVariantIdByMetalSlug(slug) {
      if (!slug) return "";
      var target = String(slug).toLowerCase().trim();
      for (var vid in metalSlugs) {
        if (Object.prototype.hasOwnProperty.call(metalSlugs, vid)) {
          if (String(metalSlugs[vid]).toLowerCase().trim() === target) {
            return String(vid);
          }
        }
      }
      return "";
    }

    function buildConfigCode() {
      var slug = getCurrentMetalSlug();
      if (!slug) return "";
      var months = [];
      for (var i = 1; i <= stoneCount; i++) {
        var sid = selections[i];
        if (!sid || !stoneById[sid]) return ""; // incomplete selection
        var m = stoneById[sid].month;
        if (!m) return "";
        months.push(m);
      }
      if (!months.length) return "";
      return slug + "_" + months.join("_");
    }

    function syncConfigCode() {
      if (!configCodeInput) return;
      configCodeInput.value = buildConfigCode();
    }

    function readConfigFromURL() {
      try {
        var code = new URL(window.location.href).searchParams.get("c");
        if (!code) return null;
        var parts = code.split("_");
        var months = [];
        while (parts.length && /^\d{1,2}$/.test(parts[parts.length - 1])) {
          months.unshift(parseInt(parts.pop(), 10));
        }
        return { metalSlug: parts.join("_"), months: months };
      } catch (e) { return null; }
    }

    function readSizeFromURL() {
      try {
        return new URL(window.location.href).searchParams.get("size") || "";
      } catch (e) { return ""; }
    }

    function syncURL() {
      try {
        var url = new URL(window.location.href);
        var code = buildConfigCode();
        if (code) url.searchParams.set("c", code);
        else url.searchParams.delete("c");

        var sizeVal = sizeSelect && sizeSelect.value ? sizeSelect.value : "";
        if (sizeVal) url.searchParams.set("size", sizeVal);
        else url.searchParams.delete("size");

        // replaceState — avoids a back-button entry per click
        history.replaceState(history.state || {}, "", url.toString());
      } catch (e) {}
    }

    // ==================================================================
    // Defaults — resolve once into a maxStones-wide array of handles.
    // Priority: URL (?c=) > product metafield (birthstone.default_stones)
    //           > repeat last > first stone in library
    // Applied to any visible slot that's empty, so the ring is never bare.
    // ==================================================================
    var resolvedDefaults = (function () {
      var urlCfg = readConfigFromURL();
      var urlMonths = (urlCfg && urlCfg.months) || [];
      var out = [];
      for (var i = 0; i < maxStones; i++) {
        var handle = "";

        if (urlMonths[i]) {
          var s = stoneByMonth[urlMonths[i]];
          if (s) handle = s.id;
        }

        if (!handle && defaultStonesMF[i]) {
          var h = String(defaultStonesMF[i]).toLowerCase().trim();
          if (stoneById[h]) handle = h;
        }

        if (!handle) {
          handle = out[i - 1] || (stones[0] && stones[0].id) || "";
        }

        out.push(handle);
      }
      return out;
    })();

    function applyDefaultToSlot(slotNum) {
      if (slotNum < 1 || slotNum > maxStones) return;
      if (selections[slotNum]) return; // already chosen — don't overwrite
      var handle = resolvedDefaults[slotNum - 1];
      if (!handle || !stoneById[handle]) return;
      var stone = stoneById[handle];
      selectStone(slotNum, stone.id, stone.name);
    }

    // ------------------------------------------------------------------
    // Validation
    // ------------------------------------------------------------------
    function validate() {
      if (stoneCount < 1) return true;

      for (var i = 1; i <= stoneCount; i++) {
        var input = root.querySelector('[data-birthstone-input="' + i + '"]');
        var val = input ? (input.value || "").trim() : "";
        if (!val) return false;
      }

      var sizeSel = root.querySelector("#BirthstoneSize");
      if (sizeSel && !sizeSel.value) return false;

      return true;
    }

    function showValidation() {
      if (validationMsg) validationMsg.classList.add("is-visible");
    }

    function hideValidation() {
      if (validationMsg) validationMsg.classList.remove("is-visible");
    }

    // Intercept form submit for validation
    if (form) {
      form.addEventListener("submit", function (e) {
        if (!validate()) {
          e.preventDefault();
          e.stopPropagation();
          showValidation();

          for (var i = 1; i <= stoneCount; i++) {
            var input = root.querySelector('[data-birthstone-input="' + i + '"]');
            if (input && !input.value.trim()) {
              var slot = root.querySelector('[data-birthstone-slot="' + i + '"]');
              if (slot) slot.scrollIntoView({ behavior: "smooth", block: "center" });
              break;
            }
          }
          return false;
        }

        hideValidation();
      });
    }

    // ------------------------------------------------------------------
    // Swap the metal-specific description block based on current variant.
    // Rich-text HTML is rendered by Liquid into <template> elements
    // (one per variant that has a value); we clone the matching one in.
    // ------------------------------------------------------------------
    function updateMetalDescription() {
      if (!metalDescEl) return;
      var id = getCurrentVariantId();
      var tpl = id ? root.querySelector('template[data-metal-description-variant="' + id + '"]') : null;
      if (tpl && tpl.innerHTML.trim() !== "") {
        metalDescEl.innerHTML = tpl.innerHTML;
        metalDescEl.removeAttribute("hidden");
      } else {
        metalDescEl.innerHTML = "";
        metalDescEl.setAttribute("hidden", "");
      }
    }

    // ------------------------------------------------------------------
    // Listen for variant changes
    // ------------------------------------------------------------------
    root.addEventListener("change", function () {
      userViewingGallery = false;
      setTimeout(function () { update(); updateMetalDescription(); }, 0);
    });

    root.addEventListener("click", function (e) {
      if (!e || !e.target) return;
      var buttonLike = e.target.closest("label, button, .variant-input, .variant__button");
      if (buttonLike) {
        userViewingGallery = false;
        setTimeout(function () { update(); updateMetalDescription(); }, 30);
      }
    });

    // Clicking a real product thumbnail pauses the compositor so the
    // shopper can view lifestyle / detail photos. Any picker change brings
    // the composite back.
    if (themeGallery) {
      themeGallery.addEventListener("click", function (e) {
        if (!e || !e.target) return;
        var thumb = e.target.closest("[data-product-thumb]");
        if (!thumb) return;
        userViewingGallery = true;
        hideCompositor();
      });
    }

    document.addEventListener("variant:change", function () {
      userViewingGallery = false;
      setTimeout(function () { update(); updateMetalDescription(); }, 30);
    });

    window.addEventListener("popstate", function () {
      setTimeout(function () { update(); updateMetalDescription(); }, 30);
    });

    // Detect URL ?variant= changes
    var lastVariantId = "";
    try {
      lastVariantId = new URL(window.location.href).searchParams.get("variant") || "";
    } catch (e) {}

    setInterval(function () {
      try {
        var currentId = new URL(window.location.href).searchParams.get("variant") || "";
        if (currentId !== lastVariantId) {
          lastVariantId = currentId;
          userViewingGallery = false;
          setTimeout(function () { update(); updateMetalDescription(); }, 30);
        }
      } catch (e) {}
    }, 250);

    // Size select also feeds the config code + URL
    if (sizeSelect) {
      sizeSelect.addEventListener("change", function () {
        syncConfigCode();
        syncURL();
      });
    }

    // ------------------------------------------------------------------
    // Init
    // ------------------------------------------------------------------
    // If ?c= is present without ?variant= (bare share link), match the
    // metal slug to a variant's birthstone.metal_slug metafield and reload
    // with ?variant=X so Shopify's native picker renders the right variant.
    // We can't change the picker selection without a reload — Shopify has
    // already bound to the default variant by the time our JS runs.
    (function hydrateVariantFromCode() {
      try {
        var url = new URL(window.location.href);
        if (url.searchParams.get("variant")) return;
        var cfg = readConfigFromURL();
        if (!cfg || !cfg.metalSlug) return;
        var vid = findVariantIdByMetalSlug(cfg.metalSlug);
        if (!vid) return;
        url.searchParams.set("variant", vid);
        window.location.replace(url.toString());
      } catch (e) {}
    })();

    // Seed the size select from URL or metafield default before update runs.
    if (sizeSelect) {
      var urlSize = readSizeFromURL();
      var desiredSize = urlSize || defaultSizeMF || "";
      if (desiredSize) {
        for (var si = 0; si < sizeSelect.options.length; si++) {
          if (sizeSelect.options[si].value === desiredSize) {
            sizeSelect.selectedIndex = si;
            break;
          }
        }
      }
    }

    update();
    updateMetalDescription();
  }

  // ====================================================================
  // Bootstrap
  // ====================================================================
  function initAll() {
    var els = document.querySelectorAll("script[data-birthstone-json]");
    if (!els || !els.length) return;

    els.forEach(function (el) {
      initBirthstoneConfigurator(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
